/* eslint-disable no-param-reassign -- https://redux-toolkit.js.org/usage/immer-reducers */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { OrderEditType } from '~/utils/constants';
import {
  orderEditActionSchema,
  orderEditCancelOrderActionSchema,
  orderEditStagedIdSchema,
  orderEditStagedSchema,
  orderEditUpdateAddressActionSchema,
} from '~/utils/schemas';
import { orderEditCommit } from './sharedActions';

export type OrderEditAction = z.infer<typeof orderEditActionSchema>;
export type OrderEditStaged = z.infer<typeof orderEditStagedIdSchema>;

export type OrderEditState = z.infer<typeof orderEditStateSchema>;

const Steps = {
  initial: 'initial',
  editing: 'editing',
  reviewing: 'reviewing',
  completed: 'completed',
} as const;

const initialState = z.object({
  step: z.literal(Steps.initial),
  editType: z.null(),
  action: z.null(),
  stagedEdits: z.null().optional(),
});

const baseEditState = z.object({
  step: z.literal(Steps.editing),
  appliedOrderEditId: z.number().optional(),
});
const editingState = z.discriminatedUnion('editType', [
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.CANCEL_ORDER}`),
    action: orderEditCancelOrderActionSchema,
    stagedEdits: z.never().optional(),
  }),
  baseEditState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
    action: orderEditUpdateAddressActionSchema,
    stagedEdits: orderEditStagedSchema.options[1].shape.stagedEdits.nullable(),
  }),
]);

const baseReviewState = z.object({
  step: z.literal(Steps.reviewing),
  appliedOrderEditId: z.number(),
});
const reviewingState = z.discriminatedUnion('editType', [
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.CANCEL_ORDER}`),
    action: orderEditCancelOrderActionSchema,
    stagedEdits: z.never().optional(),
  }),
  baseReviewState.extend({
    editType: z.literal(`${OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
    action: orderEditUpdateAddressActionSchema,
    stagedEdits: orderEditStagedSchema.options[1].shape.stagedEdits,
  }),
]);

const completedState = initialState.extend({
  step: z.literal(Steps.completed),
});

export const orderEditStateSchema = z.union([
  initialState,
  editingState,
  reviewingState,
  completedState,
]);

export const orderEditSlice = createSlice({
  name: 'orderEdit',
  initialState: {
    step: Steps.initial,
    editType: null,
    action: null,
  } as z.infer<typeof orderEditStateSchema>,
  reducers: {
    assignAction: (state, action: PayloadAction<OrderEditAction>) => {
      state.step = Steps.editing;
      state.editType = action.payload.editType;
      state.action = action.payload;
      // clear any previous staged edits
      state.stagedEdits = undefined;
    },

    assignEdits: (state, action: PayloadAction<OrderEditStaged>) => {
      if (
        !(state.step === Steps.editing || state.step === Steps.reviewing) ||
        state.editType !== action.payload.editType
      ) {
        throw new Error('State mismatch');
      }

      if (action.payload.editType !== 'CANCEL_ORDER') {
        state.stagedEdits = action.payload.stagedEdits;
      }

      state.step = Steps.reviewing;
      state.appliedOrderEditId = action.payload.appliedOrderEditId;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(orderEditCommit, () => ({
      step: Steps.completed,
      editType: null,
      action: null,
      // clear any previous staged edits
      stagedEdits: undefined,
    }));
  },
});
